import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  compileForm,
  type CompileFormResult,
} from "@bjalon/form-runtime/compiler";
import { useAuth } from "../context/AuthContext";
import { adminRouteParams, replaceAdminRouteParams } from "../lib/hashRoute";
import {
  createDeck,
  createExercise,
  createQuestionnaire,
  listPedagogicalContent,
  listPedagogicalVersions,
  publishDeckVersion,
  publishExerciseVersion,
  publishQuestionnaireVersion,
  softDeletePedagogicalContent,
  updateDeck,
  updateExercise,
  updateQuestionnaire,
} from "../services/pedagogicalContentService";
import {
  buildContentObjectTransportBundle,
  serializeFormationTransportBundle,
} from "../services/formationTransportService";
import type { Formation } from "../types/formation";
import type {
  Deck,
  Exercise,
  ExerciseResponseMode,
  PedagogicalContentKind,
  PedagogicalContentLists,
  PedagogicalVersion,
  Questionnaire,
} from "../types/pedagogicalContent";

const defaultLists: PedagogicalContentLists = {
  decks: [],
  exercises: [],
  questionnaires: [],
};

const kindLabels: Record<PedagogicalContentKind, string> = {
  deck: "Decks YAML",
  exercise: "Exercices Markdown",
  questionnaire: "Questionnaires YAML",
};

type ContentEditorMode = "create" | "edit";
type ContentPanel = "catalog" | "publication";
type PedagogicalContentItem = Deck | Exercise | Questionnaire;

const contentPanels: Array<{
  id: ContentPanel;
  label: string;
  summary: string;
}> = [
  {
    id: "catalog",
    label: "Catalogue",
    summary: "Brouillons",
  },
  {
    id: "publication",
    label: "Publication",
    summary: "Versions immuables",
  },
];

const contentKinds: PedagogicalContentKind[] = [
  "deck",
  "questionnaire",
  "exercise",
];

function readContentPanelParam(): ContentPanel {
  const panel = adminRouteParams().get("contentPanel");

  return contentPanels.some((item) => item.id === panel)
    ? (panel as ContentPanel)
    : "catalog";
}

function readContentKindParam(): PedagogicalContentKind {
  const kind = adminRouteParams().get("contentKind");

  return contentKinds.includes(kind as PedagogicalContentKind)
    ? (kind as PedagogicalContentKind)
    : "deck";
}

function formatDate(value: Date | null): string {
  if (!value) {
    return "Non disponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function splitRefs(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function versionMessage(version: PedagogicalVersion): string {
  return version.message || "Version publiée";
}

function versionTitle(version: PedagogicalVersion): string {
  return version.title || version.id;
}

function currentPublishedVersionId(
  kind: PedagogicalContentKind,
  deck: Deck | null,
  questionnaire: Questionnaire | null,
  exercise: Exercise | null,
): string | null {
  if (kind === "deck") {
    return deck?.currentPublishedVersionId ?? null;
  }

  if (kind === "questionnaire") {
    return questionnaire?.currentPublishedVersionId ?? null;
  }

  return exercise?.currentPublishedVersionId ?? null;
}

function compileQuestionnaireSource(source: string): CompileFormResult {
  return compileForm(
    {
      content: source,
      uri: "qorelia-formation://questionnaire-draft.yaml",
    },
    undefined,
    { mode: "authoring" },
  );
}

function compiledQuestionCount(result: CompileFormResult | null): number {
  if (!result || result.status === "invalid") {
    return 0;
  }

  return result.form.pages.reduce(
    (total, page) =>
      total +
      page.elements.filter((element) => element.type === "question").length,
    0,
  );
}

function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

function downloadJsonFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function PedagogicalContentWorkspace({
  formation,
}: {
  formation: Formation | null;
}) {
  const { user } = useAuth();
  const [commentedCorrectAnswer, setCommentedCorrectAnswer] = useState("");
  const [contentLists, setContentLists] =
    useState<PedagogicalContentLists>(defaultLists);
  const [correctionDeckFragment, setCorrectionDeckFragment] = useState("");
  const [correctionMarkdown, setCorrectionMarkdown] = useState("");
  const [contentSearch, setContentSearch] = useState("");
  const [exerciseRefs, setExerciseRefs] = useState("");
  const [activeContentPanel, setActiveContentPanel] =
    useState<ContentPanel>(readContentPanelParam);
  const [editorMode, setEditorMode] = useState<ContentEditorMode | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [kind, setKind] = useState<PedagogicalContentKind>(readContentKindParam);
  const [message, setMessage] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [publishMessage, setPublishMessage] = useState("");
  const [questionnaireRefs, setQuestionnaireRefs] = useState("");
  const [responseMode, setResponseMode] =
    useState<ExerciseResponseMode>("none");
  const [selectedContentId, setSelectedContentId] = useState<string | null>(
    null,
  );
  const [sourceContent, setSourceContent] = useState("");
  const [title, setTitle] = useState("");
  const [versions, setVersions] = useState<PedagogicalVersion[]>([]);

  const selectedDeck = useMemo(
    () =>
      contentLists.decks.find((deck) => deck.id === selectedContentId) ?? null,
    [contentLists.decks, selectedContentId],
  );
  const selectedQuestionnaire = useMemo(
    () =>
      contentLists.questionnaires.find(
        (questionnaire) => questionnaire.id === selectedContentId,
      ) ?? null,
    [contentLists.questionnaires, selectedContentId],
  );
  const selectedExercise = useMemo(
    () =>
      contentLists.exercises.find((exercise) => exercise.id === selectedContentId) ??
      null,
    [contentLists.exercises, selectedContentId],
  );
  const currentItems = useMemo(() => {
    if (kind === "questionnaire") {
      return contentLists.questionnaires;
    }

    if (kind === "exercise") {
      return contentLists.exercises;
    }

    return contentLists.decks;
  }, [contentLists, kind]);
  const filteredContentItems = useMemo(() => {
    const search = contentSearch.trim().toLowerCase();

    if (!search) {
      return currentItems;
    }

    return currentItems.filter((item) => item.title.toLowerCase().includes(search));
  }, [contentSearch, currentItems]);
  const selectedContentItem: PedagogicalContentItem | null =
    kind === "deck"
      ? selectedDeck
      : kind === "questionnaire"
        ? selectedQuestionnaire
        : selectedExercise;
  const publishedVersionId = currentPublishedVersionId(
    kind,
    selectedDeck,
    selectedQuestionnaire,
    selectedExercise,
  );
  const questionnaireCompileResult = useMemo(
    () =>
      kind === "questionnaire"
        ? compileQuestionnaireSource(sourceContent)
        : null,
    [kind, sourceContent],
  );

  useEffect(() => {
    function handleHashChange() {
      setActiveContentPanel(readContentPanelParam());
      setKind(readContentKindParam());
    }

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (!formation) {
      setContentLists(defaultLists);
      setSelectedContentId(null);
      return;
    }

    setIsLoading(true);
    setMessage(null);
    void listPedagogicalContent(formation.id)
      .then((nextLists) => {
        setContentLists(nextLists);
        setSelectedContentId((currentId) => {
          const allIds = [
            ...nextLists.decks,
            ...nextLists.questionnaires,
            ...nextLists.exercises,
          ].map((item) => item.id);

          return currentId && allIds.includes(currentId)
            ? currentId
            : nextLists.decks[0]?.id ?? null;
        });
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger les contenus pédagogiques.");
      })
      .finally(() => setIsLoading(false));
  }, [formation]);

  useEffect(() => {
    setEditorMode(null);
    setSelectedContentId(currentItems[0]?.id ?? null);
  }, [currentItems, kind]);

  useEffect(() => {
    if (kind === "deck" && selectedDeck) {
      setTitle(selectedDeck.title);
      setSourceContent(selectedDeck.draftContent);
      setPageCount(selectedDeck.pageCount);
      setQuestionnaireRefs(selectedDeck.questionnaireRefs.join(", "));
      setExerciseRefs(selectedDeck.exerciseRefs.join(", "));
      setCorrectionDeckFragment("");
      setCommentedCorrectAnswer("");
      setCorrectionMarkdown("");
      setResponseMode("none");
      return;
    }

    if (kind === "questionnaire" && selectedQuestionnaire) {
      setTitle(selectedQuestionnaire.title);
      setSourceContent(selectedQuestionnaire.draftDefinition);
      setCorrectionDeckFragment(selectedQuestionnaire.correctionDeckFragment);
      setCommentedCorrectAnswer(selectedQuestionnaire.commentedCorrectAnswer);
      setCorrectionMarkdown("");
      setResponseMode("none");
      return;
    }

    if (kind === "exercise" && selectedExercise) {
      setTitle(selectedExercise.title);
      setSourceContent(selectedExercise.draftStatement);
      setCorrectionDeckFragment(selectedExercise.correctionDeckFragment);
      setCorrectionMarkdown(selectedExercise.correctionMarkdown);
      setResponseMode(selectedExercise.responseMode);
      setCommentedCorrectAnswer("");
      return;
    }

    setTitle("");
    setSourceContent("");
    setPageCount(0);
    setQuestionnaireRefs("");
    setExerciseRefs("");
    setCorrectionDeckFragment("");
    setCommentedCorrectAnswer("");
    setCorrectionMarkdown("");
    setResponseMode("none");
  }, [kind, selectedDeck, selectedExercise, selectedQuestionnaire]);

  useEffect(() => {
    if (!formation || !selectedContentId) {
      setVersions([]);
      return;
    }

    void listPedagogicalVersions({
      contentId: selectedContentId,
      formationId: formation.id,
      kind,
    })
      .then(setVersions)
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger l'historique des versions.");
      });
  }, [formation, kind, selectedContentId, publishedVersionId]);

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!formation || !user) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      if (kind === "deck") {
        const draft = {
          draftContent: sourceContent,
          exerciseRefs: splitRefs(exerciseRefs),
          pageCount,
          questionnaireRefs: splitRefs(questionnaireRefs),
          title,
        };
        const nextDeck = selectedDeck
          ? await updateDeck({
              actorUid: user.uid,
              deckId: selectedDeck.id,
              draft,
              formationId: formation.id,
            })
          : await createDeck({
              actorUid: user.uid,
              draft,
              formationId: formation.id,
            });

        setContentLists((current) => ({
          ...current,
          decks: [
            nextDeck,
            ...current.decks.filter((deck) => deck.id !== nextDeck.id),
          ],
        }));
        setSelectedContentId(nextDeck.id);
      }

      if (kind === "questionnaire") {
        const draft = {
          commentedCorrectAnswer,
          correctionDeckFragment,
          draftDefinition: sourceContent,
          title,
        };
        const nextQuestionnaire = selectedQuestionnaire
          ? await updateQuestionnaire({
              actorUid: user.uid,
              draft,
              formationId: formation.id,
              questionnaireId: selectedQuestionnaire.id,
            })
          : await createQuestionnaire({
              actorUid: user.uid,
              draft,
              formationId: formation.id,
            });

        setContentLists((current) => ({
          ...current,
          questionnaires: [
            nextQuestionnaire,
            ...current.questionnaires.filter(
              (questionnaire) => questionnaire.id !== nextQuestionnaire.id,
            ),
          ],
        }));
        setSelectedContentId(nextQuestionnaire.id);
      }

      if (kind === "exercise") {
        const draft = {
          correctionDeckFragment,
          correctionMarkdown,
          draftStatement: sourceContent,
          responseMode,
          title,
        };
        const nextExercise = selectedExercise
          ? await updateExercise({
              actorUid: user.uid,
              draft,
              exerciseId: selectedExercise.id,
              formationId: formation.id,
            })
          : await createExercise({
              actorUid: user.uid,
              draft,
              formationId: formation.id,
            });

        setContentLists((current) => ({
          ...current,
          exercises: [
            nextExercise,
            ...current.exercises.filter(
              (exercise) => exercise.id !== nextExercise.id,
            ),
          ],
        }));
        setSelectedContentId(nextExercise.id);
      }

      setEditorMode(null);
      setMessage("Contenu enregistré.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible d'enregistrer ce contenu.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handlePublish() {
    if (!user) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      if (kind === "deck" && selectedDeck) {
        const version = await publishDeckVersion({
          actorUid: user.uid,
          deck: selectedDeck,
          message: publishMessage,
        });
        setVersions((current) => [version, ...current]);
      }

      if (kind === "questionnaire" && selectedQuestionnaire) {
        const version = await publishQuestionnaireVersion({
          actorUid: user.uid,
          message: publishMessage,
          questionnaire: selectedQuestionnaire,
        });
        setVersions((current) => [version, ...current]);
      }

      if (kind === "exercise" && selectedExercise) {
        const version = await publishExerciseVersion({
          actorUid: user.uid,
          exercise: selectedExercise,
          message: publishMessage,
        });
        setVersions((current) => [version, ...current]);
      }

      if (formation) {
        const nextLists = await listPedagogicalContent(formation.id);
        setContentLists(nextLists);
      }

      setPublishMessage("");
      setMessage("Version publiée.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible de publier cette version.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(targetContentId: string | null = selectedContentId) {
    if (!formation || !user || !targetContentId) {
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      await softDeletePedagogicalContent({
        actorUid: user.uid,
        contentId: targetContentId,
        formationId: formation.id,
        kind,
      });
      const nextLists = await listPedagogicalContent(formation.id);
      setContentLists(nextLists);
      setEditorMode(null);
      setSelectedContentId((currentId) =>
        currentId === targetContentId ? null : currentId,
      );
      setMessage("Contenu supprimé logiquement.");
    } catch (error) {
      console.error(error);
      setMessage("Impossible de supprimer ce contenu.");
    } finally {
      setIsSaving(false);
    }
  }

  function handleExportObject(content: PedagogicalContentItem | null = selectedContentItem) {
    if (!formation || !content) {
      return;
    }

    const bundle = buildContentObjectTransportBundle({
      content,
      formation,
      kind,
    });

    downloadJsonFile(
      `${slugify(content.title) || kind}-export-v1.json`,
      serializeFormationTransportBundle(bundle),
    );
    setMessage("Export objet préparé.");
  }

  function openCreateEditor() {
    setEditorMode("create");
    setSelectedContentId(null);
    setTitle("");
    setSourceContent("");
    setPageCount(0);
    setQuestionnaireRefs("");
    setExerciseRefs("");
    setCorrectionDeckFragment("");
    setCommentedCorrectAnswer("");
    setCorrectionMarkdown("");
    setResponseMode("none");
    setMessage(null);
  }

  function openEditEditor(contentId: string) {
    setEditorMode("edit");
    setSelectedContentId(contentId);
    setMessage(null);
  }

  function closeEditor() {
    setEditorMode(null);
    setSelectedContentId((currentId) => currentId ?? currentItems[0]?.id ?? null);
  }

  function openContentPanel(panel: ContentPanel) {
    setActiveContentPanel(panel);
    replaceAdminRouteParams({ contentPanel: panel });
  }

  function openContentKind(nextKind: PedagogicalContentKind) {
    setKind(nextKind);
    replaceAdminRouteParams({ contentKind: nextKind });
  }

  if (!formation) {
    return (
      <section className="panel">
        <p className="eyebrow">Contenus pédagogiques</p>
        <h2>Sélection requise</h2>
        <p>Sélectionnez une formation pour gérer ses contenus.</p>
      </section>
    );
  }

  return (
    <section className="pedagogy-workspace">
      <article className="panel panel-wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Contenus pédagogiques</p>
            <h2>{formation.title}</h2>
          </div>
          <button
            className="button button-primary"
            onClick={openCreateEditor}
            type="button"
          >
            Nouveau contenu
          </button>
        </div>
        <div className="detail-tabs" role="tablist" aria-label="Sections contenus">
          {contentPanels.map((panel) => (
            <button
              aria-selected={activeContentPanel === panel.id}
              className={
                activeContentPanel === panel.id
                  ? "detail-tab detail-tab-active"
                  : "detail-tab"
              }
              key={panel.id}
              onClick={() => openContentPanel(panel.id)}
              type="button"
            >
              <span>{panel.label}</span>
              <small>{panel.summary}</small>
            </button>
          ))}
        </div>
        {activeContentPanel === "catalog" ? (
          <>
            <div className="list-toolbar">
              <div className="segmented" role="tablist" aria-label="Type de contenu">
                {(["deck", "questionnaire", "exercise"] as const).map((nextKind) => (
                  <button
                    aria-selected={kind === nextKind}
                    className={kind === nextKind ? "active" : ""}
                    key={nextKind}
                    onClick={() => openContentKind(nextKind)}
                    type="button"
                  >
                    {kindLabels[nextKind]}
                  </button>
                ))}
              </div>
              <label className="field list-filter">
                <span>Filtrer</span>
                <input
                  onChange={(event) => setContentSearch(event.target.value)}
                  placeholder="Titre du contenu"
                  type="search"
                  value={contentSearch}
                />
              </label>
              <div className="list-summary">
                <strong>{filteredContentItems.length}</strong>
                <span>objet(s)</span>
              </div>
            </div>
            {isLoading ? <p>Chargement des contenus...</p> : null}
            {filteredContentItems.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Objet</th>
                      <th scope="col">Publication</th>
                      <th scope="col">Type</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContentItems.map((item) => (
                      <tr
                        className={item.id === selectedContentId ? "row-selected" : ""}
                        key={item.id}
                      >
                        <td>
                          <button
                            className="table-title-button"
                            onClick={() => setSelectedContentId(item.id)}
                            type="button"
                          >
                            <strong>{item.title}</strong>
                            <span>{item.id}</span>
                          </button>
                        </td>
                        <td>{item.currentPublishedVersionId ?? "Non publié"}</td>
                        <td>{kindLabels[kind]}</td>
                        <td>
                          <div className="row-actions">
                            <button
                              className="button button-secondary button-small"
                              onClick={() => setSelectedContentId(item.id)}
                              type="button"
                            >
                              Ouvrir
                            </button>
                            <button
                              className="button button-secondary button-small"
                              onClick={() => openEditEditor(item.id)}
                              type="button"
                            >
                              Modifier
                            </button>
                            <button
                              className="button button-secondary button-small"
                              onClick={() => handleExportObject(item)}
                              type="button"
                            >
                              Exporter
                            </button>
                            <button
                              className="button button-secondary button-small"
                              disabled={isSaving}
                              onClick={() => void handleDelete(item.id)}
                              type="button"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Aucun contenu ne correspond aux filtres.</p>
            )}
          </>
        ) : null}
        {message ? (
          <div className="alert alert-info" role="status">
            {message}
          </div>
        ) : null}
      </article>

      {editorMode ? (
        <div className="modal-backdrop">
          <section
            aria-labelledby="content-editor-title"
            aria-modal="true"
            className="modal-panel modal-panel-large"
            role="dialog"
          >
            <div className="section-heading">
              <div>
                <p className="eyebrow">{kindLabels[kind]}</p>
                <h2 id="content-editor-title">
                  {editorMode === "create" ? "Créer un brouillon" : "Modifier le brouillon"}
                </h2>
              </div>
              <button
                className="button button-secondary button-small"
                disabled={isSaving}
                onClick={closeEditor}
                type="button"
              >
                Fermer
              </button>
            </div>
            <form className="stack" onSubmit={handleSave}>
              <label className="field">
                <span>Titre</span>
                <input
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  type="text"
                  value={title}
                />
              </label>
              <label className="field">
                <span>
                  {kind === "exercise"
                    ? "Énoncé Markdown"
                    : kind === "questionnaire"
                      ? "Définition YAML"
                      : "Deck YAML"}
                </span>
                <textarea
                  onChange={(event) => setSourceContent(event.target.value)}
                  rows={12}
                  value={sourceContent}
                />
              </label>
              {kind === "deck" ? (
                <div className="form-grid">
                  <label className="field">
                    <span>Nombre de pages</span>
                    <input
                      min={0}
                      onChange={(event) => setPageCount(Number(event.target.value))}
                      type="number"
                      value={pageCount}
                    />
                  </label>
                  <label className="field">
                    <span>Questionnaires référencés</span>
                    <input
                      onChange={(event) => setQuestionnaireRefs(event.target.value)}
                      placeholder="id1, id2"
                      type="text"
                      value={questionnaireRefs}
                    />
                  </label>
                  <label className="field">
                    <span>Exercices référencés</span>
                    <input
                      onChange={(event) => setExerciseRefs(event.target.value)}
                      placeholder="id1, id2"
                      type="text"
                      value={exerciseRefs}
                    />
                  </label>
                </div>
              ) : null}
          {kind === "questionnaire" ? (
            <>
              <div className="form-grid">
                <label className="field">
                  <span>Fragment deck correction</span>
                  <textarea
                    onChange={(event) =>
                      setCorrectionDeckFragment(event.target.value)
                    }
                    rows={5}
                    value={correctionDeckFragment}
                  />
                </label>
                <label className="field">
                  <span>Réponses commentées</span>
                  <textarea
                    onChange={(event) =>
                      setCommentedCorrectAnswer(event.target.value)
                    }
                    rows={5}
                    value={commentedCorrectAnswer}
                  />
                </label>
              </div>
              <div className="runtime-preview">
                <div>
                  <p className="eyebrow">Runtime questionnaire</p>
                  <h3>
                    {questionnaireCompileResult?.status === "valid"
                      ? "Compilation valide"
                      : questionnaireCompileResult?.status === "degraded"
                        ? "Compilation avec alertes"
                        : "Compilation invalide"}
                  </h3>
                  <p>
                    {questionnaireCompileResult?.status === "invalid"
                      ? "Le YAML sera conservé, mais il doit être corrigé avant usage runtime."
                      : `${questionnaireCompileResult?.form.pages.length ?? 0} page(s), ${compiledQuestionCount(questionnaireCompileResult)} question(s).`}
                  </p>
                </div>
                {questionnaireCompileResult?.diagnostics.length ? (
                  <ul className="compact-list">
                    {questionnaireCompileResult.diagnostics.map(
                      (diagnostic, index) => (
                        <li key={`${diagnostic.code}-${index}`}>
                          <span>{diagnostic.severity}</span>
                          <small>{diagnostic.message}</small>
                        </li>
                      ),
                    )}
                  </ul>
                ) : null}
              </div>
            </>
          ) : null}
          {kind === "exercise" ? (
            <div className="form-grid">
              <label className="field">
                <span>Mode de réponse</span>
                <select
                  onChange={(event) =>
                    setResponseMode(event.target.value as ExerciseResponseMode)
                  }
                  value={responseMode}
                >
                  <option value="none">Aucune réponse attendue</option>
                  <option value="markdown">Réponse Markdown</option>
                </select>
              </label>
              <label className="field">
                <span>Correction Markdown</span>
                <textarea
                  onChange={(event) => setCorrectionMarkdown(event.target.value)}
                  rows={5}
                  value={correctionMarkdown}
                />
              </label>
              <label className="field">
                <span>Fragment deck correction</span>
                <textarea
                  onChange={(event) =>
                    setCorrectionDeckFragment(event.target.value)
                  }
                  rows={5}
                  value={correctionDeckFragment}
                />
              </label>
            </div>
          ) : null}
          <div className="actions">
            <button className="button button-primary" disabled={isSaving} type="submit">
              Enregistrer
            </button>
            <button
              className="button button-secondary"
              disabled={isSaving || !selectedContentId}
              onClick={() => void handleDelete(selectedContentId)}
              type="button"
            >
              Supprimer logiquement
            </button>
            <button
              className="button button-secondary"
              disabled={!selectedContentId}
              onClick={() => handleExportObject()}
              type="button"
            >
              Exporter objet
            </button>
          </div>
        </form>
          </section>
        </div>
      ) : null}

      {activeContentPanel === "publication" ? (
      <article className="panel panel-wide">
        <p className="eyebrow">Publication</p>
        <h2>Versions immuables</h2>
        <label className="field">
          <span>Message de version</span>
          <input
            onChange={(event) => setPublishMessage(event.target.value)}
            type="text"
            value={publishMessage}
          />
        </label>
        <button
          className="button button-primary"
          disabled={isSaving || !selectedContentId}
          onClick={handlePublish}
          type="button"
        >
          Publier une version
        </button>
        {versions.length > 0 ? (
          <ul className="version-list">
            {versions.map((version) => (
              <li key={version.id}>
                <strong>{versionTitle(version)}</strong>
                <span>{formatDate(version.createdAt)}</span>
                <p>{versionMessage(version)}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p>Aucune version publiée.</p>
        )}
      </article>
      ) : null}
    </section>
  );
}
